'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Copy, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

interface LoginFields {
  email: string;
  remember: boolean;
}

const DEMO_EMAIL = 'support@courtcraftadvocate.com';

export default function LoginForm() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<'email' | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFields>({ defaultValues: { remember: false } });

  useEffect(() => {
    router.prefetch('/cases-dashboard');
  }, [router]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(DEMO_EMAIL);
    setCopiedField('email');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = () => {
    setValue('email', DEMO_EMAIL);
    toast.info('Demo email filled in');
  };

  const onSubmit = async (data: LoginFields) => {
    setLoading(true);

    if (data.email) {
      toast.success('Welcome back');
      router.push('/cases-dashboard');
    } else {
      setError('root', {
        message: 'Please enter your email address to sign in',
      });
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      <div>
        <h2 className="text-2xl font-700 text-foreground mb-1">Welcome back</h2>
        <p className="text-sm text-muted-foreground">Sign in to your LAICSAI regulation workspace</p>
      </div>

      {errors.root && (
        <div
          role="alert"
          className="flex items-start gap-2 p-3 rounded-lg bg-danger-bg border border-danger/20 text-sm text-danger"
        >
          <AlertCircle size={15} className="shrink-0 mt-0.5" aria-hidden="true" />
          <span>{errors.root.message}</span>
        </div>
      )}

      {/* Email */}
      <div>
        <label htmlFor="login-email" className="block text-sm font-500 text-foreground mb-1.5">
          Email address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@chambers.co.uk"
          className={`w-full px-3.5 py-2.5 rounded-lg border text-sm bg-card transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
            errors.email ? 'border-danger' : 'border-input hover:border-muted-foreground'
          }`}
          {...register('email', {
            required: 'Email address is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p role="alert" className="text-xs text-danger mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2">
        <input
          id="remember-me"
          type="checkbox"
          className="w-4 h-4 rounded border-input accent-secondary"
          {...register('remember')}
        />
        <label htmlFor="remember-me" className="text-sm text-muted-foreground">
          Keep me signed in for 30 days
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 bg-primary text-white text-sm font-600 rounded-lg hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ minHeight: '42px' }}
      >
        {loading ? (
          <>
            <Loader2 size={15} className="animate-spin" aria-hidden="true" />
            <span>Signing in…</span>
          </>
        ) : (
          'Sign In'
        )}
      </button>

      {/* Demo credentials box */}
      <div className="border border-border rounded-xl p-4 bg-muted">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-600 text-foreground">Demo credentials</p>
          <button
            type="button"
            onClick={autofill}
            className="text-xs text-secondary font-medium hover:underline"
          >
            Autofill
          </button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-card rounded-lg px-3 py-2 border border-border">
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Email</p>
              <p className="text-xs font-500 text-foreground font-mono">{DEMO_EMAIL}</p>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              aria-label="Copy demo email"
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              {copiedField === 'email' ? (
                <CheckCircle size={13} className="text-success" aria-hidden="true" />
              ) : (
                <Copy size={13} aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}